import type { ShouldRevalidateFunction } from "~/remix";
import { Outlet, json, useLoaderData, useParams, useRouteLoaderData } from "~/remix";

import type { LoaderFunctionArgs } from "@remix-run/node";
import invariant from "tiny-invariant";
import { getTheme } from "~/helpers/theme.server";
import { getGameBySlug, getRefreshModeFromGame } from "~/models/game.server";
import { getGameSettings } from "~/models/setting.server";
import { getUserOrLogout, requireUser } from "~/session.server";

import type { ThemeDef } from "~/components/Theme/ThemeSelect";
import { emitter } from "~/helpers/emitter.server";
import { useThemeChange } from "~/hook/useThemeChange";
import { useRootLoaderData } from "~/root";
import { useGetAppHash } from "~/utils";

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  formAction
}) => {
  if (formAction?.includes('/player')) {
    return false;
  }
  return defaultShouldRevalidate;
};

export const useSlugLoaderData = () => {
  const ret = useRouteLoaderData<typeof loader>('routes/game/$slug');
  return ret;
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  const { slug } = params;
  invariant(slug, 'Slug must be defined');
  const [game, slugTheme, user] = await Promise.all([
  getGameBySlug(slug ?? ''),
  getTheme(request, slug),
  getUserOrLogout(request),
  requireUser(request)]
  );

  invariant(game, 'Game not found');
  const [tmp, tmpRefreshMode, themes0] = await Promise.all([
  getGameSettings(game?.id ?? 0),
  getRefreshModeFromGame(game),
  getGameSettings(0)]
  );

  let refreshMode = tmpRefreshMode;

  if (refreshMode === 'AUTO') {
    if (user.roles.includes('role_guest')) {
      refreshMode = 'BUTTON';
    }
  }
  const availableThemes = (tmp.get('themes') ?? themes0.get('themes') ?? []) as ThemeDef[];
  const { gameMasterId } = game;

  let theme = slugTheme;

  const isSSEEnabled = refreshMode === 'AUTO';

  return json({
    availableThemes,
    slugTheme: theme,
    refreshMode,
    slug,
    gameId: game.id,
    gameMasterId,
    isSSEEnabled
  }, {
    headers: {

      // 'Cache-Control': 'public, max-age=60'
    } });
}

export const useSlugRouteLoaderData = () => {
  const ret = useRouteLoaderData<typeof loader>("routes/game/$slug");
  invariant(ret, "Unable to read $slug loader");
  return ret;
};

export default function IndexPage() {
  const { slug } = useParams();
  const { slugTheme, gameId } = useLoaderData<typeof loader>();
  const hash = useGetAppHash();
  const {
    version
  } = useRootLoaderData();
  useThemeChange(slugTheme);

  if (!slug) return undefined;

  return <div data-layout="play">
    <link rel="stylesheet" href={`/customs/${gameId}/style.css?=v=${version}&hash=${hash}`} />
    <Outlet />
  </div>;
}